import { z } from "zod";
import type { ConversationAction, ConversationActionScore, ReviewThreshold, ScoringConfigFormValues } from "./types";

export const FREQUENCY_OPTIONS = [
  { value: "daily", label: "daily" },
  { value: "weekly", label: "weekly" },
] as const;

export const WEEKDAY_OPTIONS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
] as const;

export const CONVERSATION_ACTION_OPTIONS: {
  value: ConversationAction;
  label: string;
}[] = [
  { value: "send_message", label: "reply" },
  { value: "resolve_conversation", label: "conversation closed" },
  { value: "assign_conversation", label: "conversation assigned" },
];

export const DEFAULT_CONVERSATION_ACTIONS: ConversationActionScore[] = [
  { action: "send_message", points: 2 },
  { action: "resolve_conversation", points: 10 },
];

export const DEFAULT_REVIEW_THRESHOLDS: ReviewThreshold[] = [
  { points: 30, percent: 50 },
  { points: 45, percent: 60 },
  { points: 60, percent: 70 },
];

export const scoringConfigFormSchema = z.object({
  notificationFrequency: z.enum(["daily", "weekly"]),
  messagingDay: z.enum([
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ]),
  notificationTime: z.string().regex(/^\d{2}:\d{2}$/, "Invalid time format"),
  notificationTimezone: z.string().min(1, "Timezone is required"),
  conversationActions: z
    .array(
      z.object({
        action: z.enum(["send_message", "resolve_conversation", "assign_conversation"]),
        points: z.number().int().min(0),
      }),
    )
    .min(0),
  reviewThresholds: z
    .array(
      z.object({
        points: z.number().int().min(0),
        percent: z.number().int().min(0).max(100),
      }),
    )
    .min(0),
});

export const DEFAULT_SCORING_FORM_VALUES: ScoringConfigFormValues = {
  notificationFrequency: "weekly",
  messagingDay: "monday",
  notificationTime: "09:00",
  notificationTimezone: "Europe/London",
  conversationActions: [],
  reviewThresholds: [],
};

// Major world timezones ordered by UTC offset
export const TIMEZONES = [
  { value: "Etc/GMT+12", label: "International Date Line West (UTC-12)" },
  { value: "Pacific/Pago_Pago", label: "Samoa Standard Time (UTC-11)" },
  { value: "Pacific/Honolulu", label: "Hawaii-Aleutian Time (HST, UTC-10)" },
  { value: "Pacific/Marquesas", label: "Marquesas Islands Time (UTC-9:30)" },
  { value: "America/Anchorage", label: "Alaska Time (AKST/AKDT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PST/PDT)" },
  { value: "America/Denver", label: "Mountain Time (MST/MDT)" },
  { value: "America/Phoenix", label: "Mountain Standard Time (MST, UTC-7, no DST)" },
  { value: "America/Chicago", label: "Central Time (CST/CDT)" },
  { value: "America/New_York", label: "Eastern Time (EST/EDT)" },
  { value: "America/Bogota", label: "Colombia Standard Time (COT)" },
  { value: "America/Halifax", label: "Atlantic Time (AST/ADT)" },
  { value: "America/Caracas", label: "Venezuela Standard Time (VET)" },
  { value: "America/St_Johns", label: "Newfoundland Time (NST/NDT)" },
  { value: "America/Argentina/Buenos_Aires", label: "Argentina Standard Time (ART)" },
  { value: "America/Sao_Paulo", label: "Brasilia Standard Time (BRT/BRST)" },
  { value: "America/Noronha", label: "Fernando de Noronha Time (FNT)" },
  { value: "Atlantic/Azores", label: "Azores Standard Time (AZOT/AZOST)" },
  { value: "UTC", label: "Coordinated Universal Time (UTC)" },
  { value: "Europe/London", label: "Greenwich Mean Time (GMT/BST)" },
  { value: "Africa/Lagos", label: "West Africa Standard Time (WAT)" },
  { value: "Europe/Paris", label: "Central European Time (CET/CEST)" },
  { value: "Africa/Harare", label: "Central Africa Time (CAT)" },
  { value: "Africa/Johannesburg", label: "South Africa Standard Time (SAST)" },
  { value: "Africa/Cairo", label: "Egypt Standard Time (EET)" },
  { value: "Europe/Helsinki", label: "Eastern European Time (EET/EEST)" },
  { value: "Asia/Jerusalem", label: "Israel Standard Time (IST/IDT)" },
  { value: "Europe/Moscow", label: "Moscow Standard Time (MSK)" },
  { value: "Africa/Nairobi", label: "East Africa Time (EAT)" },
  { value: "Asia/Riyadh", label: "Arabia Standard Time (AST)" },
  { value: "Europe/Istanbul", label: "Turkey Time (TRT, UTC+3)" },
  { value: "Asia/Tehran", label: "Iran Standard Time (IRST/IRDT)" },
  { value: "Asia/Dubai", label: "Gulf Standard Time (GST)" },
  { value: "Asia/Kabul", label: "Afghanistan Standard Time (AFT)" },
  { value: "Asia/Yekaterinburg", label: "Yekaterinburg Standard Time (YEKT)" },
  { value: "Asia/Karachi", label: "Pakistan Standard Time (PKT)" },
  { value: "Asia/Kolkata", label: "India Standard Time (IST)" },
  { value: "Asia/Kathmandu", label: "Nepal Standard Time (NPT)" },
  { value: "Asia/Dhaka", label: "Bangladesh Standard Time (BST)" },
  { value: "Asia/Yangon", label: "Myanmar Standard Time (MMT)" },
  { value: "Asia/Bangkok", label: "Indochina Time (ICT)" },
  { value: "Asia/Jakarta", label: "Western Indonesia Time (WIB)" },
  { value: "Asia/Shanghai", label: "China Standard Time (CST)" },
  { value: "Asia/Singapore", label: "Singapore Standard Time (SGT)" },
  { value: "Asia/Kuala_Lumpur", label: "Malaysia Time (MYT)" },
  { value: "Asia/Manila", label: "Philippine Standard Time (PST)" },
  { value: "Australia/Perth", label: "Australian Western Standard Time (AWST)" },
  { value: "Asia/Tokyo", label: "Japan Standard Time (JST)" },
  { value: "Asia/Seoul", label: "Korea Standard Time (KST)" },
  { value: "Australia/Adelaide", label: "Australian Central Time (ACST/ACDT)" },
  { value: "Australia/Brisbane", label: "Australian Eastern Time (AEST Queensland)" },
  { value: "Australia/Sydney", label: "Australian Eastern Time (AEST/AEDT)" },
  { value: "Pacific/Guam", label: "Chamorro Standard Time (ChST)" },
  { value: "Pacific/Guadalcanal", label: "Solomon Islands Time (SBT)" },
  { value: "Pacific/Auckland", label: "New Zealand Standard Time (NZST/NZDT)" },
  { value: "Pacific/Fiji", label: "Fiji Standard Time (FJT)" },
  { value: "Pacific/Tongatapu", label: "Tonga Standard Time (TOT)" },
  { value: "Pacific/Apia", label: "Samoa Standard Time (WST, UTC+13)" },
] as const;

// Time options for the dropdown (15-minute increments)
export const TIME_OPTIONS = Array.from({ length: 24 * 4 }, (_, i) => {
  const hours = Math.floor(i / 4)
    .toString()
    .padStart(2, "0");
  const minutes = ((i % 4) * 15).toString().padStart(2, "0");
  return { value: `${hours}:${minutes}`, label: `${hours}:${minutes}` };
});

